// nodejs library to set properties for components
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
// react plugin used to create datetimepicker
import ReactDatetime from 'react-datetime';
// @mui/material components
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Input from '@mui/material/Input';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import withStyles from '@mui/styles/withStyles';
// @mui/icons-material components
import Calendar from '@mui/icons-material/CalendarToday';
// nodejs library that concatenates classes
import classNames from 'classnames';
// @mui/material components
import { makeStyles } from '@mui/styles';
import styles from 'assets/jss/material-dashboard-pro-react/components/customInputStyle.js';

const useStyles = makeStyles(styles);

const style = {
  inputAdornmentIcon: {
    color: '#555',
  },
  inputAdornment: {
    position: 'relative',
  },
};

const formatDisplayValue = (value) => {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? value : parsedDate.toDateString();
  }

  if (value?._isAMomentObject && typeof value.toDate === 'function') {
    const parsedDate = value.toDate();
    return Number.isNaN(parsedDate.getTime()) ? '' : parsedDate.toDateString();
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : value.toDateString();
  }

  return '';
};

function Datepicker(props) {
  const classes = useStyles();

  const {
    formControlProps,
    labelText,
    id,
    labelProps,
    inputProps,
    validator,
    error,
    white,
    inputRootCustomClasses,
    success,
    helperText,
  } = props;

  const {
    value: inputValue,
    onChange: inputOnChange,
    ...otherInputProps
  } = inputProps || {};

  const [displayValue, setDisplayValue] = useState(formatDisplayValue(inputValue));

  useEffect(() => {
    setDisplayValue(formatDisplayValue(inputValue));
  }, [inputValue]);

  const handleDateChange = (value) => {
    setDisplayValue(typeof value === 'string' ? value : formatDisplayValue(value));

    if (typeof inputOnChange === 'function') {
      inputOnChange(value);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Backspace' && displayValue !== '') {
      e.preventDefault();
      setDisplayValue('');
      if (typeof inputOnChange === 'function') {
        inputOnChange('');
      }
    }
  };

  const labelClasses = classNames({
    [' ' + classes.labelRootError]: error,
    [' ' + classes.labelRootSuccess]: success && !error,
  });
  const underlineClasses = classNames({
    [classes.underlineError]: error,
    [classes.underlineSuccess]: success && !error,
    [classes.underline]: true,
    [classes.whiteUnderline]: white,
  });
  const marginTop = classNames({
    [inputRootCustomClasses]: inputRootCustomClasses !== undefined,
  });
  const inputClasses = classNames({
    [classes.input]: true,
    [classes.whiteInput]: white,
  });
  var formControlClasses;
  if (formControlProps !== undefined) {
    formControlClasses = classNames(
      formControlProps.className,
      classes.formControl
    );
  } else {
    formControlClasses = classes.formControl;
  }
  var helpTextClasses = classNames({
    [classes.labelRootError]: error,
    [classes.labelRootSuccess]: success && !error,
  });

  return (
    (<ReactDatetime
      timeFormat={false}
      closeOnSelect={true}
      isValidDate={validator}
      {...otherInputProps}
      value={displayValue}
      onChange={handleDateChange}
      renderInput={(dateInputProps, open, close) => (
        <FormControl variant="standard" {...formControlProps} className={formControlClasses}>
          {labelText !== undefined ? (
            <InputLabel
              className={classes.labelRoot + ' ' + labelClasses}
              htmlFor={id}
              {...labelProps}
            >
              {labelText}
            </InputLabel>
          ) : null}

          <Input
            {...dateInputProps}
            {...otherInputProps}
            value={displayValue}
            onChange={dateInputProps.onChange}
            onKeyDown={handleKeyDown}
            autoComplete='off'
            className=''
            classes={{
              input: inputClasses,
              root: marginTop,
              disabled: classes.disabled,
              underline: underlineClasses,
            }}
            id={id}
            endAdornment={
              props.adornment === true ? (
                <InputAdornment
                  position='end'
                  className={classes.inputAdornment}
                >
                  <Calendar className={classes.inputAdornmentIcon} />
                </InputAdornment>
              ) : (
                ''
              )
            }
          />

          {helperText !== undefined ? (
            <FormHelperText id={id + '-text'} className={helpTextClasses}>
              {helperText}
            </FormHelperText>
          ) : null}
        </FormControl>
      )}
    />)
  );
}
Datepicker.propTypes = {
  labelText: PropTypes.node,
  labelProps: PropTypes.object,
  validator: PropTypes.func,
  id: PropTypes.string,
  inputProps: PropTypes.object,
  formControlProps: PropTypes.object,
  inputRootCustomClasses: PropTypes.string,
  error: PropTypes.bool,
  success: PropTypes.bool,
  white: PropTypes.bool,
  helperText: PropTypes.node,
};

export default withStyles(style)(Datepicker);
